import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  Alert,
  Button as MuiButton,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SortIcon from '@mui/icons-material/Sort';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HistoryIcon from '@mui/icons-material/History';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import { tenantApi } from '../../api/tenantApi';
import axiosClient from '../../api/axiosClient';
import { Tenant } from '../../types/tenant';
import { useTenant } from '../../context/TenantContext';
import { useEnv } from '../../context/EnvContext';
import { paths } from '../../routes/paths';
import { colors, typo, primaryButtonSx } from '../../theme/tokens';
import { EmptyState, WriteButton } from '../../components/ui';
import SunnyLogo from '../../components/SunnyLogo';
import EnvDropdown from '../Layout/EnvDropdown';
import { useCurrentUser } from '../../auth/useCurrentUser';
import { useAppLogout } from '../../auth/useAppLogout';
import { firstGrantedSurfacePath } from '../../auth/permissionCatalog';

import CreateTenantWizard from './CreateTenantWizard';
import SyncFromPlatformModal from './SyncFromPlatformModal';
import CreateCustomerDialog from './CreateCustomerDialog';
import CreateSponsorDialog from './CreateSponsorDialog';
import TenantCard from './TenantCard';

type SortOption = 'name-asc' | 'name-desc' | 'recent' | 'id';

/**
 * The one tenant-type filter the browse tiers share (MNS-1276). A single value,
 * not two booleans, so "Prod only" and "Synthetic only" cannot both be on —
 * turning one on turns the other off.
 */
export type TenantTypeFilter = 'all' | 'prod' | 'synthetic';

export function matchesTenantTypeFilter(tenant: Pick<Tenant, 'is_prod_tenant' | 'is_synthetic'>, filter: TenantTypeFilter): boolean {
  if (filter === 'prod') return tenant.is_prod_tenant === true;
  if (filter === 'synthetic') return tenant.is_synthetic === true;
  return true;
}
type Tier = 'customers' | 'sponsors' | 'tenants';

interface Customer {
  customer_id: string;
  customer_code: string;
  customer_name: string;
  customer_description: string;
}

interface Sponsor {
  sponsor_id: string;
  customer_id: string;
  sponsor_code: string;
  sponsor_name: string;
  sponsor_description: string;
}

interface BucketCardData {
  key: string;
  code: string | null;
  name: string;
  description: string;
  tenantCount: number;
  sponsorCount?: number;
  entityId: string;
  updatedAt: string | null;
  actionLabel: string;
  icon: 'customer' | 'sponsor';
  helper?: string;
  recentRank: number;
}

const SORT_LABELS: Record<SortOption, string> = {
  'name-asc': 'Name A-Z',
  'name-desc': 'Name Z-A',
  recent: 'Recently Updated',
  id: 'ID',
};

const RECENT_TENANTS_KEY = 'sunnyRewards_recentTenants';
const MAX_RECENT = 10;
const UNASSIGNED_ROUTE_VALUE = '~unassigned';
const UNASSIGNED_LABEL = 'Unassigned';

const routeValue = (code: string | null): string => encodeURIComponent(code || UNASSIGNED_ROUTE_VALUE);
const paramToCode = (value?: string): string | null => {
  if (!value || value === UNASSIGNED_ROUTE_VALUE) return null;
  return value;
};
const customerSponsorsPath = (customerCode: string | null): string =>
  `/customers/${routeValue(customerCode)}/sponsors`;
const sponsorTenantsPath = (customerCode: string | null, sponsorCode: string | null): string =>
  `/customers/${routeValue(customerCode)}/sponsors/${routeValue(sponsorCode)}/tenants`;

const getRecentTenantCodes = (): string[] => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(RECENT_TENANTS_KEY) : null;
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const addRecentTenant = (code: string): void => {
  try {
    const recent = getRecentTenantCodes().filter((c) => c !== code);
    recent.unshift(code);
    window.localStorage.setItem(RECENT_TENANTS_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {
    /* storage unavailable - silently skip */
  }
};

const latestDate = (tenants: Tenant[]): string | null =>
  tenants.reduce<string | null>((latest, tenant) => {
    if (!tenant.versioned_at) return latest;
    if (!latest) return tenant.versioned_at;
    return new Date(tenant.versioned_at).getTime() > new Date(latest).getTime()
      ? tenant.versioned_at
      : latest;
  }, null);

const firstLetter = (name: string): string => name.charAt(0).toUpperCase() || 'U';

const BucketCard: React.FC<{ item: BucketCardData; onSelect: (item: BucketCardData) => void }> = ({
  item,
  onSelect,
}) => {
  const Icon = item.icon === 'customer' ? DomainOutlinedIcon : GroupsOutlinedIcon;
  const isRecent = item.recentRank !== -1;
  const isMostRecent = item.recentRank === 0;

  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      aria-label={`${item.actionLabel} ${item.name}`}
      onClick={() => onSelect(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(item);
        }
      }}
      sx={{
        p: 2.5,
        borderRadius: '12px',
        border: `1px solid ${colors.borderDefault}`,
        cursor: 'pointer',
        minHeight: 172,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'all 0.2s ease',
        bgcolor: colors.bgWhite,
        '&:hover': {
          borderColor: colors.brand,
          boxShadow: '0 8px 24px rgba(20,20,23,0.08)',
          transform: 'translateY(-2px)',
        },
        '&:focus-visible': {
          outline: `2px solid ${colors.brand}`,
          outlineOffset: 2,
        },
      }}
    >
      {isRecent && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: '6px',
            bgcolor: isMostRecent ? colors.brandLight : colors.bgMuted,
            border: `1px solid ${isMostRecent ? 'rgba(250,179,6,0.2)' : colors.borderLight}`,
          }}
        >
          <HistoryIcon sx={{ fontSize: 12, color: isMostRecent ? colors.brand : colors.textTertiary }} />
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              fontFamily: 'Manrope, sans-serif',
              color: isMostRecent ? colors.brandHover : colors.textTertiary,
            }}
          >
            {isMostRecent ? 'Last used' : 'Recent'}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            bgcolor: item.code ? colors.brandLight : colors.bgMuted,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: item.code ? colors.warningText : colors.textSecondary,
            fontWeight: 800,
            fontFamily: 'Manrope, sans-serif',
            flexShrink: 0,
          }}
        >
          {item.code ? firstLetter(item.name) : <Icon sx={{ fontSize: 22 }} />}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ ...typo.sectionTitle, lineHeight: 1.25 }}>{item.name}</Typography>
          {!item.code && (
            <Chip
              label="No code"
              size="small"
              sx={{ mt: 0.75, height: 20, fontSize: 10, borderRadius: '6px' }}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <Typography sx={{ ...typo.secondaryText, mb: 0.25 }}>{item.description}</Typography>
        <Typography sx={{ ...typo.mono, fontSize: 12, color: colors.textSecondary }}>
          {item.code || 'unassigned'}
        </Typography>
        {item.helper && <Typography sx={{ ...typo.smallText, mt: 0.5 }}>{item.helper}</Typography>}
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ ...typo.smallText, color: colors.textPrimary }}>{item.actionLabel}</Typography>
          <ChevronRightIcon sx={{ fontSize: 16, color: colors.textSecondary }} />
        </Box>
      </Box>
    </Paper>
  );
};

const SelectTenant: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ customerCode?: string; sponsorCode?: string }>();
  const { setActiveTenantCode } = useTenant();
  const currentUser = useCurrentUser();
  const { rbacV2Enabled, hasPermission } = currentUser;
  const bareChrome = rbacV2Enabled && hasPermission('hide_chrome');
  const showAdminEntry = currentUser.isSuperAdmin && currentUser.adminViewEnabled;
  const appLogout = useAppLogout();
  const queryClient = useQueryClient();
  const { currentEnv } = useEnv();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [createSponsorOpen, setCreateSponsorOpen] = useState(false);
  const [sort, setSort] = useState<SortOption>('name-asc');
  const [typeFilter, setTypeFilter] = useState<TenantTypeFilter>('all');
  const toggleTypeFilter = (f: Exclude<TenantTypeFilter, 'all'>) => setTypeFilter((cur) => (cur === f ? 'all' : f));
  const [recentCodes, setRecentCodes] = useState<string[]>(() => getRecentTenantCodes());

  const selectedCustomerCode = paramToCode(params.customerCode);
  const selectedSponsorCode = paramToCode(params.sponsorCode);
  const tier: Tier = params.sponsorCode ? 'tenants' : params.customerCode ? 'sponsors' : 'customers';

  useEffect(() => {
    setSearch('');
  }, [tier, params.customerCode, params.sponsorCode]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === RECENT_TENANTS_KEY) setRecentCodes(getRecentTenantCodes());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Key the picker lists by the selected env so switching the EnvDropdown
  // refetches against that env (the cross-env axios interceptor proxies these
  // GETs to the chosen env's read gateway). Without currentEnv in the key the
  // dropdown would be cosmetic — you'd keep seeing the home env's tenants.
  const { data: tenants, isLoading: tenantsLoading, isError, refetch } = useQuery({
    queryKey: ['tenants', currentEnv],
    queryFn: () => tenantApi.getAll(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: customers = [], isLoading: customersLoading } = useQuery<Customer[]>({
    queryKey: ['lookup-customers', currentEnv],
    queryFn: async () => {
      const { data } = await axiosClient.get('/api/lookup/customers');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: sponsors = [], isLoading: sponsorsLoading } = useQuery<Sponsor[]>({
    queryKey: ['lookup-sponsors', currentEnv],
    queryFn: async () => {
      const { data } = await axiosClient.get('/api/lookup/sponsors');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const customerByCode = useMemo(
    () => new Map(customers.map((customer) => [customer.customer_code, customer])),
    [customers],
  );
  const customerIdByCode = useMemo(
    () => new Map(customers.map((customer) => [customer.customer_code, customer.customer_id])),
    [customers],
  );
  const sponsorByCode = useMemo(
    () => new Map(sponsors.map((sponsor) => [sponsor.sponsor_code, sponsor])),
    [sponsors],
  );
  const selectedCustomerName = selectedCustomerCode
    ? customerByCode.get(selectedCustomerCode)?.customer_name || selectedCustomerCode
    : UNASSIGNED_LABEL;
  const selectedSponsorName = selectedSponsorCode
    ? sponsorByCode.get(selectedSponsorCode)?.sponsor_name || selectedSponsorCode
    : UNASSIGNED_LABEL;

  const handleSelect = useCallback(
    (tenant: Tenant) => {
      addRecentTenant(tenant.tenant_code);
      setRecentCodes(getRecentTenantCodes());
      setActiveTenantCode(tenant.tenant_code);
      navigate(
        bareChrome
          ? firstGrantedSurfacePath(currentUser, tenant.tenant_code)
          : paths.dashboard(tenant.tenant_code),
      );
    },
    [navigate, setActiveTenantCode, bareChrome, currentUser],
  );

  const handleCreated = useCallback(
    (tenantCode: string) => {
      setCreateOpen(false);
      addRecentTenant(tenantCode);
      setRecentCodes(getRecentTenantCodes());
      setActiveTenantCode(tenantCode);
      navigate(paths.dashboard(tenantCode));
    },
    [navigate, setActiveTenantCode],
  );

  const handleImported = useCallback(
    (tenantCode: string) => {
      setSyncOpen(false);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      addRecentTenant(tenantCode);
      setRecentCodes(getRecentTenantCodes());
      setActiveTenantCode(tenantCode);
      navigate(paths.dashboard(tenantCode));
    },
    [navigate, setActiveTenantCode, queryClient],
  );

  const customerCards = useMemo<BucketCardData[]>(() => {
    // When "Prod only" / "Synthetic only" is on, group from just those tenants —
    // so customers with none drop out and the sponsor/tenant counts reflect them.
    const source = (tenants || []).filter((t) => matchesTenantTypeFilter(t, typeFilter));
    const groups = new Map<string, Tenant[]>();
    source.forEach((tenant) => {
      const key = tenant.customer_code || UNASSIGNED_ROUTE_VALUE;
      groups.set(key, [...(groups.get(key) || []), tenant]);
    });

    return Array.from(groups.entries()).map(([key, group]) => {
      const code = key === UNASSIGNED_ROUTE_VALUE ? null : key;
      const customer = code ? customerByCode.get(code) : null;
      const sponsorCodes = new Set(group.map((tenant) => tenant.sponsor_code || UNASSIGNED_ROUTE_VALUE));
      const sponsorCount = sponsorCodes.size;
      const ranks = group.map((t) => recentCodes.indexOf(t.tenant_code)).filter((r) => r !== -1);
      const recentRank = ranks.length > 0 ? Math.min(...ranks) : -1;
      return {
        key,
        code,
        name: customer?.customer_name || code || UNASSIGNED_LABEL,
        description: `${sponsorCount} sponsor${sponsorCount === 1 ? '' : 's'} · ${group.length} tenant${group.length === 1 ? '' : 's'}`,
        tenantCount: group.length,
        sponsorCount,
        entityId: customer?.customer_id || code || '',
        updatedAt: latestDate(group),
        actionLabel: 'View sponsors',
        icon: 'customer',
        recentRank,
      };
    });
  }, [tenants, customerByCode, recentCodes, typeFilter]);

  const sponsorCards = useMemo<BucketCardData[]>(() => {
    const scopedTenants = (tenants || []).filter((tenant) =>
      (selectedCustomerCode ? tenant.customer_code === selectedCustomerCode : !tenant.customer_code)
      // "Prod only" / "Synthetic only": drop sponsors with no such tenant; counts reflect it.
      && matchesTenantTypeFilter(tenant, typeFilter),
    );
    const groups = new Map<string, Tenant[]>();
    scopedTenants.forEach((tenant) => {
      const key = tenant.sponsor_code || UNASSIGNED_ROUTE_VALUE;
      groups.set(key, [...(groups.get(key) || []), tenant]);
    });
    const selectedCustomerId = selectedCustomerCode ? customerIdByCode.get(selectedCustomerCode) : null;

    return Array.from(groups.entries()).map(([key, group]) => {
      const code = key === UNASSIGNED_ROUTE_VALUE ? null : key;
      const lookupSponsor = code ? sponsorByCode.get(code) : null;
      const ranks = group.map((t) => recentCodes.indexOf(t.tenant_code)).filter((r) => r !== -1);
      const recentRank = ranks.length > 0 ? Math.min(...ranks) : -1;
      return {
        key,
        code,
        name: lookupSponsor?.sponsor_name || code || UNASSIGNED_LABEL,
        description: `${group.length} tenant${group.length === 1 ? '' : 's'}`,
        tenantCount: group.length,
        entityId: lookupSponsor?.sponsor_id || code || '',
        updatedAt: latestDate(group),
        actionLabel: 'View tenants',
        icon: 'sponsor',
        helper:
          lookupSponsor && selectedCustomerId && lookupSponsor.customer_id !== selectedCustomerId
            ? 'Sponsor lookup belongs to another customer'
            : undefined,
        recentRank,
      };
    });
  }, [tenants, selectedCustomerCode, customerIdByCode, sponsorByCode, recentCodes, typeFilter]);

  const scopedTenants = useMemo(
    () =>
      (tenants || []).filter((tenant) => {
        const customerMatches = selectedCustomerCode
          ? tenant.customer_code === selectedCustomerCode
          : !tenant.customer_code;
        const sponsorMatches = selectedSponsorCode
          ? tenant.sponsor_code === selectedSponsorCode
          : !tenant.sponsor_code;
        return customerMatches && sponsorMatches;
      }),
    [tenants, selectedCustomerCode, selectedSponsorCode],
  );

  const activeCards = tier === 'customers' ? customerCards : sponsorCards;

  const filteredCards = useMemo(() => {
    const q = search.toLowerCase();
    const matched = !q
      ? activeCards
      : activeCards.filter((item) =>
          `${item.name} ${item.code || 'unassigned'}`.toLowerCase().includes(q),
        );

    return [...matched].sort((a, b) => {
      switch (sort) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'recent':
          return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
        case 'id':
          return a.entityId.localeCompare(b.entityId, undefined, { numeric: true });
        default:
          return 0;
      }
    });
  }, [activeCards, search, sort]);

  const filteredTenants = useMemo(() => {
    const q = search.toLowerCase();
    const base = scopedTenants.filter((t) => matchesTenantTypeFilter(t, typeFilter));
    const matched = !q
      ? base
      : base.filter((tenant) =>
          `${tenant.tenant_name} ${tenant.tenant_code} ${tenant.tenant_id || ''}`.toLowerCase().includes(q),
        );

    return [...matched].sort((a, b) => {
      switch (sort) {
        case 'name-asc':
          return a.tenant_name.localeCompare(b.tenant_name);
        case 'name-desc':
          return b.tenant_name.localeCompare(a.tenant_name);
        case 'recent':
          return new Date(b.versioned_at || 0).getTime() - new Date(a.versioned_at || 0).getTime();
        case 'id':
          return (a.tenant_id || 0) - (b.tenant_id || 0);
        default:
          return 0;
      }
    });
  }, [scopedTenants, search, sort, typeFilter]);

  const isLoading = tenantsLoading || customersLoading || sponsorsLoading;
  const resultCount = tier === 'tenants' ? filteredTenants.length : filteredCards.length;
  const totalTenantCount = tenants?.length || 0;

  const title = tier === 'customers' ? 'Choose a customer' : tier === 'sponsors' ? 'Choose a sponsor' : 'Open a tenant';
  const subtitle =
    tier === 'customers'
      ? 'Start with the customer, then drill into their sponsors and tenants.'
      : tier === 'sponsors'
        ? `Sponsors under ${selectedCustomerName}.`
        : `Tenants under ${selectedSponsorName}.`;
  const tierLabel = tier === 'customers' ? 'Tier 1 · Customer' : tier === 'sponsors' ? 'Tier 2 · Sponsor' : 'Tier 3 · Tenant';
  const placeholder =
    tier === 'customers'
      ? 'Search customers or codes...'
      : tier === 'sponsors'
        ? `Search sponsors under ${selectedCustomerName}...`
        : `Search tenants under ${selectedSponsorName}...`;

  const emptyTitle = tier === 'customers' ? 'No customers found' : tier === 'sponsors' ? 'No sponsors found' : 'No tenants found';
  const emptyDescription = search
    ? `No results for "${search}". Try a different search term.`
    : tier === 'customers'
      ? 'No customer groups are available for your tenants.'
      : tier === 'sponsors'
        ? 'No sponsor groups are available under this customer.'
        : 'No tenants are available under this customer and sponsor.';

  // Keep the trail on a SINGLE line: the ol defaults to flex-wrap:wrap and the
  // crumbs have no nowrap/ellipsis, so long customer/sponsor names wrapped onto
  // several lines (with chevrons stranded on their own line) inside the narrow
  // centered box. nowrap + per-crumb ellipsis (+ title tooltip) fixes it; the
  // container gets minWidth:0 below so the crumbs can actually shrink.
  const crumbLinkSx = {
    ...typo.secondaryText,
    color: colors.textSecondary,
    display: 'block',
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as const;
  const breadcrumb = (
    <Breadcrumbs
      separator={<ChevronRightIcon sx={{ fontSize: 14 }} />}
      sx={{
        maxWidth: '100%',
        overflow: 'hidden',
        '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap', justifyContent: 'center' },
        '& .MuiBreadcrumbs-li': { minWidth: 0, overflow: 'hidden' },
        '& .MuiBreadcrumbs-separator': { mx: 0.5, flexShrink: 0 },
      }}
    >
      {tier !== 'customers' && (
        <Link
          component="button"
          underline="hover"
          onClick={() => navigate(paths.root)}
          sx={{ ...crumbLinkSx, flexShrink: 0, maxWidth: 'none' }}
        >
          Customers
        </Link>
      )}
      {tier !== 'customers' && (
        <Link
          component="button"
          underline="hover"
          title={selectedCustomerName}
          onClick={() => navigate(customerSponsorsPath(selectedCustomerCode))}
          sx={crumbLinkSx}
        >
          {selectedCustomerName}
        </Link>
      )}
      {tier === 'tenants' && (
        <Link
          component="button"
          underline="hover"
          title={selectedSponsorName}
          onClick={() => navigate(sponsorTenantsPath(selectedCustomerCode, selectedSponsorCode))}
          sx={{ ...crumbLinkSx, fontWeight: 800, color: colors.textPrimary }}
        >
          {selectedSponsorName}
        </Link>
      )}
    </Breadcrumbs>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: colors.bgPage }}>
      <Box
        sx={{
          minHeight: 56,
          bgcolor: colors.bgWhite,
          borderBottom: `1px solid ${colors.borderLight}`,
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2, md: 3 },
          py: 1,
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <SunnyLogo height={26} />
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Typography sx={{ fontSize: 13, color: colors.textSecondary, fontWeight: 500 }}>
          Client Portal
        </Typography>

        <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
          {breadcrumb}
        </Box>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* hide_chrome users see only Sign out — no env, create, or admin controls. */}
          {!bareChrome && (
            <>
              {/* Environment selector — available to all users, matching TopBar.tsx.
                  Per-env reachability is enforced inside EnvDropdown (non-readable
                  envs are disabled), and every remote env's API still applies its
                  own auth/role checks, so no role gate is needed here. */}
              <Typography sx={{ ...typo.tinyText, color: colors.textTertiary, mr: 0.25, display: { xs: 'none', sm: 'block' } }}>
                Environment
              </Typography>
              <EnvDropdown isCompact />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <WriteButton
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                onClick={() => setCreateOpen(true)}
                sx={{ ...primaryButtonSx, minHeight: 36, px: 2, py: 0.75, borderRadius: '8px' }}
              >
                New tenant
              </WriteButton>
              <WriteButton
                variant="outlined"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                ignoreEnvReadOnly
                onClick={() => setCreateCustomerOpen(true)}
                sx={{
                  textTransform: 'none', fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 13,
                  borderColor: colors.borderDefault, color: colors.textSecondary, borderRadius: '8px', px: 1.5, py: 0.5,
                  '&:hover': { borderColor: colors.textSecondary, backgroundColor: colors.bgMuted },
                }}
              >
                New customer
              </WriteButton>
              <WriteButton
                variant="outlined"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                ignoreEnvReadOnly
                onClick={() => setCreateSponsorOpen(true)}
                sx={{
                  textTransform: 'none', fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 13,
                  borderColor: colors.borderDefault, color: colors.textSecondary, borderRadius: '8px', px: 1.5, py: 0.5,
                  '&:hover': { borderColor: colors.textSecondary, backgroundColor: colors.bgMuted },
                }}
              >
                New sponsor
              </WriteButton>
              {showAdminEntry && (
                <MuiButton
                  variant="outlined"
                  size="small"
                  startIcon={<AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18 }} />}
                  onClick={() => navigate(paths.admin.home())}
                  sx={{
                    textTransform: 'none',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 600,
                    fontSize: 13,
                    borderColor: colors.adminAccent,
                    color: colors.adminAccent,
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.5,
                    '&:hover': {
                      borderColor: colors.adminAccent,
                      backgroundColor: colors.adminAccentSubtle,
                    },
                  }}
                >
                  Admin view
                </MuiButton>
              )}
              {/* Sync from Platform — available to any authenticated user (not
                  super-admin gated), consistent with publish/import/export. */}
              <WriteButton
                variant="outlined"
                size="small"
                startIcon={<CloudDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                ignoreEnvReadOnly
                onClick={() => setSyncOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 600,
                  fontSize: 13,
                  borderColor: colors.borderDefault,
                  color: colors.textSecondary,
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.5,
                  '&:hover': {
                    borderColor: colors.textSecondary,
                    backgroundColor: colors.bgMuted,
                  },
                }}
              >
                Sync from Platform
              </WriteButton>
            </>
          )}
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<LogoutOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={appLogout}
            sx={{
              textTransform: 'none',
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              fontSize: 13,
              borderColor: colors.borderDefault,
              color: colors.textSecondary,
              borderRadius: '8px',
              px: 1.5,
              py: 0.5,
              '&:hover': {
                borderColor: colors.textSecondary,
                backgroundColor: colors.bgMuted,
              },
            }}
          >
            Sign out
          </MuiButton>
        </Box>

        <Box sx={{ width: '100%', display: { xs: 'flex', md: 'none' }, justifyContent: 'center', pt: 0.5 }}>
          {breadcrumb}
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1120, mx: 'auto', py: { xs: 4, md: 6 }, px: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              bgcolor: colors.brandLight,
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <BusinessOutlinedIcon sx={{ color: colors.warningText, fontSize: 28 }} />
          </Box>
          <Typography sx={{ ...typo.pageTitle, mb: 1 }}>{title}</Typography>
          <Typography sx={{ ...typo.pageSubtitle, fontSize: 15, maxWidth: 560, mx: 'auto' }}>
            {subtitle}
          </Typography>
          <Chip
            label={tierLabel}
            size="small"
            sx={{
              mt: 2.5,
              bgcolor: tier === 'tenants' ? colors.infoBgStrong : colors.brandLight,
              color: tier === 'tenants' ? colors.infoText : colors.warningText,
              fontWeight: 800,
              textTransform: 'uppercase',
              fontSize: 11,
              borderRadius: '8px',
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1.5,
            mb: 3,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            // Browsing/filtering the tenant list is a READ — keep it typeable on
            // read-only envs (ReadOnlyGuard blocks beforeinput document-wide otherwise).
            inputProps={{ 'data-readonly-interactive': 'true' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textTertiary }} />
                </InputAdornment>
              ),
              sx: {
                fontSize: 14,
                borderRadius: '12px',
                bgcolor: colors.bgWhite,
                width: { xs: '100%', sm: 560 },
                fontFamily: 'Manrope, sans-serif',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              },
            }}
            size="small"
          />
          <Select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            size="small"
            startAdornment={<SortIcon sx={{ color: colors.textTertiary, fontSize: 18, mr: 0.5 }} />}
            sx={{
              fontSize: 13,
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              borderRadius: '12px',
              bgcolor: colors.bgWhite,
              minWidth: 160,
            }}
          >
            {(Object.entries(SORT_LABELS) as [SortOption, string][]).map(([key, label]) => (
              <MenuItem key={key} value={key} sx={{ fontSize: 13, fontFamily: 'Manrope, sans-serif' }}>
                {label}
              </MenuItem>
            ))}
          </Select>
          {/* "Prod only" and "Synthetic only" apply at every tier: customers/sponsors
              are filtered to those that contain such a tenant; the tenant tier shows
              those tenants. One filter value, so turning one on turns the other off. */}
          <Chip
            label="Prod only"
            size="small"
            clickable
            color={typeFilter === 'prod' ? 'warning' : 'default'}
            variant={typeFilter === 'prod' ? 'filled' : 'outlined'}
            onClick={() => toggleTypeFilter('prod')}
            sx={{ fontWeight: 600, borderRadius: '10px', height: 34, fontFamily: 'Manrope, sans-serif' }}
          />
          <Chip
            label="Synthetic only"
            size="small"
            clickable
            color={typeFilter === 'synthetic' ? 'info' : 'default'}
            variant={typeFilter === 'synthetic' ? 'filled' : 'outlined'}
            onClick={() => toggleTypeFilter('synthetic')}
            sx={{ fontWeight: 600, borderRadius: '10px', height: 34, fontFamily: 'Manrope, sans-serif' }}
          />
        </Box>

        {!isLoading && resultCount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2,
                py: 0.5,
                borderRadius: '8px',
                bgcolor: colors.bgMuted,
              }}
            >
              <KeyboardIcon sx={{ fontSize: 14, color: colors.textTertiary }} />
              <Typography sx={{ fontSize: 11, color: colors.textTertiary, fontFamily: 'Manrope, sans-serif' }}>
                Tab through cards, Enter to open
              </Typography>
            </Box>
          </Box>
        )}

        {search && !isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: 13, color: colors.textSecondary, fontFamily: 'Manrope, sans-serif' }}>
              {resultCount} result{resultCount !== 1 ? 's' : ''} for &ldquo;{search}&rdquo;
            </Typography>
          </Box>
        )}

        {isError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <MuiButton color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </MuiButton>
            }
          >
            Failed to load tenants. Please check your connection and try again.
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : resultCount === 0 ? (
          <EmptyState
            variant="search"
            title={emptyTitle}
            description={totalTenantCount === 0 && !search ? 'No tenant organizations have been created yet.' : emptyDescription}
            actionLabel={totalTenantCount === 0 && !search ? 'Create tenant' : undefined}
            onAction={totalTenantCount === 0 && !search ? () => setCreateOpen(true) : undefined}
          />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: tier === 'tenants' ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
              },
              gap: 2,
              maxWidth: tier === 'tenants' ? 760 : 'none',
              mx: 'auto',
            }}
          >
            {tier === 'tenants'
              ? filteredTenants.map((tenant) => (
                  <TenantCard
                    key={tenant.tenant_code}
                    tenant={tenant}
                    recentRank={recentCodes.indexOf(tenant.tenant_code)}
                    onSelect={handleSelect}
                  />
                ))
              : filteredCards.map((item) => (
                  <BucketCard
                    key={item.key}
                    item={item}
                    onSelect={(selected) => {
                      if (tier === 'customers') navigate(customerSponsorsPath(selected.code));
                      else navigate(sponsorTenantsPath(selectedCustomerCode, selected.code));
                    }}
                  />
                ))}
          </Box>
        )}
      </Box>

      <CreateTenantWizard
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        initialCustomerCode={tier === 'customers' ? undefined : selectedCustomerCode || undefined}
        initialSponsorCode={tier === 'tenants' ? selectedSponsorCode || undefined : undefined}
      />

      <SyncFromPlatformModal
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        onImported={handleImported}
      />

      <CreateCustomerDialog
        open={createCustomerOpen}
        onClose={() => setCreateCustomerOpen(false)}
      />
      <CreateSponsorDialog
        open={createSponsorOpen}
        onClose={() => setCreateSponsorOpen(false)}
        defaultCustomerId={tier !== 'customers' ? (customers.find((c) => c.customer_code === selectedCustomerCode)?.customer_id ?? null) : null}
      />
    </Box>
  );
};

export default SelectTenant;
